import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Filter } from 'lucide-react';
import api from '../lib/api';
import Header from '../components/Layout/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { format } from 'date-fns';

const Projects = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProjects();
  }, []);

  useEffect(() => {
    filterProjects();
  }, [projects, searchTerm, statusFilter]);

  const loadProjects = async () => {
    try {
      const response = await api.get('/projects');
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to load projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterProjects = () => {
    let filtered = projects;

    if (statusFilter !== 'All') {
      if (statusFilter === 'Fully Paid') {
        filtered = filtered.filter((p) => p.payment_status === 'Fully Paid');
      } else if (statusFilter === 'Pending Payments') {
        filtered = filtered.filter((p) => p.payment_status !== 'Fully Paid');
      } else {
        filtered = filtered.filter((p) => p.status === statusFilter);
      }
    }

    if (searchTerm) {
      filtered = filtered.filter(
        (p) =>
          p.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredProjects(filtered);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      Active: 'bg-blue-50 text-blue-700 border-blue-100',
      Completed: 'bg-emerald-50 text-emerald-700 border-emerald-100',
      'On Hold': 'bg-amber-50 text-amber-700 border-amber-100',
      Cancelled: 'bg-red-50 text-red-700 border-red-100',
    };
    return statusConfig[status] || statusConfig.Active;
  };

  const getPaymentStatusBadge = (status) => {
    const statusConfig = {
      'Fully Paid': { bg: 'bg-emerald-50 text-emerald-700 border-emerald-100', icon: '✅' },
      'Waiting': { bg: 'bg-amber-50 text-amber-700 border-amber-100', icon: '⏳' },
      'Pending': { bg: 'bg-red-50 text-red-700 border-red-100', icon: '🚨' },
    };
    return statusConfig[status] || statusConfig.Pending;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading projects...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Header title="Projects" subtitle="Manage your projects and track payments" />

      <div className="p-8">
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" strokeWidth={1.5} />
            <Input
              placeholder="Search projects..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-white border-slate-200 h-11"
              data-testid="projects-search-input"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            {['All', 'Active', 'Completed', 'Fully Paid', 'Pending Payments'].map((filter) => (
              <Button
                key={filter}
                onClick={() => setStatusFilter(filter)}
                variant={statusFilter === filter ? 'default' : 'outline'}
                className={`${
                  statusFilter === filter
                    ? 'bg-orange-500 hover:bg-orange-600 text-white'
                    : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
                } active:scale-95 transition-transform`}
                data-testid={`filter-${filter.toLowerCase().replace(' ', '-')}`}
              >
                {filter}
              </Button>
            ))}
          </div>
          <Button
            onClick={() => navigate('/projects/new')}
            className="bg-orange-500 hover:bg-orange-600 text-white active:scale-95 transition-transform"
            data-testid="add-project-button"
          >
            <Plus className="w-5 h-5 mr-2" strokeWidth={1.5} />
            Add Project
          </Button>
        </div>

        <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="projects-table">
              <thead className="bg-slate-50 text-slate-500 text-xs font-semibold uppercase tracking-wider">
                <tr>
                  <th className="px-6 py-4 text-left">Project Name</th>
                  <th className="px-6 py-4 text-right">Total Value</th>
                  <th className="px-6 py-4 text-right">Received</th>
                  <th className="px-6 py-4 text-right">Due Balance</th>
                  <th className="px-6 py-4 text-center">Payment Status</th>
                  <th className="px-6 py-4 text-center">Status</th>
                  <th className="px-6 py-4 text-center">Last Payment</th>
                </tr>
              </thead>
              <tbody>
                {filteredProjects.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center">
                      <div className="text-slate-400">
                        <p className="text-lg font-medium mb-2">No projects found</p>
                        <p className="text-sm">Start by adding your first project</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredProjects.map((project) => {
                    const paymentStatus = getPaymentStatusBadge(project.payment_status);
                    return (
                      <tr
                        key={project.id}
                        onClick={() => navigate(`/projects/${project.id}`)}
                        className="hover:bg-slate-50/50 transition-colors border-b border-slate-100 cursor-pointer"
                        data-testid={`project-row-${project.id}`}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="font-semibold text-slate-900">{project.name}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-slate-700 font-semibold">
                          {formatCurrency(project.total_value)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-emerald-600 font-semibold">
                          {formatCurrency(project.received_amount)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-red-600 font-semibold">
                          {formatCurrency(project.due_balance)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 text-xs font-medium rounded-full border ${paymentStatus.bg}`}>
                            <span>{paymentStatus.icon}</span>
                            {project.payment_status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className={`px-2.5 py-0.5 text-xs font-medium rounded-full border ${getStatusBadge(project.status)}`}>
                            {project.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-slate-600">
                          {project.last_payment_date ? format(new Date(project.last_payment_date), 'MMM dd, yyyy') : '-'}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Projects;
